/**
 * Nachholen: Projekte der letzten 90 Tage, deren Kunde noch nie um eine
 * Bewertung gebeten wurde (Benjamin, 28.09.2026: „als Auswahlliste"). Nur für
 * die angehakten entsteht eine Aufgabe. Je Kunde steht das jüngste Projekt da.
 */
import { useEffect, useState } from "react";
import { fehlerText } from "@vimaana/ui";

import { useBewertungNachholen, useBewertungNachholenAnlegen } from "../../hooks/useBewertung";
import "../../styles/post.css";
import "../../styles/bewertung.css";

function datumDe(iso: string): string {
  const [j, m, t] = (iso || "").slice(0, 10).split("-");
  return j && m && t ? `${t}.${m}.${j}` : iso;
}

export default function BewertungNachholen({ onClose }: { onClose: () => void }) {
  const q = useBewertungNachholen();
  const anlegen = useBewertungNachholenAnlegen();
  const [wahl, setWahl] = useState<Set<string>>(() => new Set());
  const projekte = q.data?.projekte || [];

  useEffect(() => {
    const zu = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", zu);
    return () => window.removeEventListener("keydown", zu);
  }, [onClose]);

  const umschalten = (id: string) => setWahl((vorher) => {
    const neu = new Set(vorher);
    if (neu.has(id)) neu.delete(id); else neu.add(id);
    return neu;
  });

  const fertig = anlegen.data;

  return (
    <div className="vg-schreib-schleier" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <section className="vg-schreib" role="dialog" aria-modal="true" aria-label="Bewertungen nachholen">
        <header className="vg-schreib-kopf">
          <div>
            <p className="vg-schreib-ueber">Bewertung erbitten</p>
            <h2>Abgeschlossen, noch nicht gefragt</h2>
          </div>
          <button type="button" className="vg-schreib-zu" onClick={onClose} aria-label="Schließen">×</button>
        </header>

        {fertig ? (
          <div className="vg-schreib-fertig">
            <p>{fertig.angelegt === 0 ? "Erledigt — niemand wird nachträglich gefragt."
              : `${fertig.angelegt === 1 ? "Eine Aufgabe" : `${fertig.angelegt} Aufgaben`} „Bewertung erbitten“ angelegt.`}</p>
            <button type="button" className="vg-btn vg-btn-primary" onClick={onClose}>Schließen</button>
          </div>
        ) : (
          <>
            <p className="vg-schreib-hinweis">
              Projekte der letzten {q.data?.tage ?? 90} Tage. Für jedes angehakte entsteht eine Aufgabe mit der fertigen Mail.
              Die Liste gibt es nur einmal: wer nicht angehakt ist, wird nicht gefragt. Ab jetzt entsteht die Aufgabe bei jedem Abschluss von selbst.
            </p>
            {q.isLoading && <p className="vg-schreib-hinweis">Lädt …</p>}
            {q.error && <p className="vg-post-fehler">{fehlerText(q.error)}</p>}
            {q.data && projekte.length === 0 && <p className="vg-schreib-hinweis">Nichts nachzuholen.</p>}
            {projekte.length > 0 && (
              <ul className="bw-liste">
                {projekte.map((p) => (
                  <li key={p.projekt_id}>
                    <label className="bw-zeile">
                      <input type="checkbox" checked={wahl.has(p.projekt_id)} onChange={() => umschalten(p.projekt_id)} />
                      <span className="bw-text">
                        <strong>{p.kunde || "—"}</strong>
                        <span className="bw-meta">
                          {p.titel}{p.abschluss ? ` · abgeschlossen ${datumDe(p.abschluss)}` : ""}
                        </span>
                      </span>
                      <span className="bw-marken">
                        {p.pflege && <span className="bw-marke">Pflege</span>}
                        {!p.hat_mail && <span className="bw-marke is-warn">keine Mail</span>}
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
            )}
            {anlegen.error && <p className="vg-post-fehler">{fehlerText(anlegen.error)}</p>}
            <footer className="vg-schreib-fuss">
              {projekte.length > 1 && (
                <button type="button" className="vg-btn vg-btn-ghost"
                  onClick={() => setWahl(wahl.size === projekte.length ? new Set() : new Set(projekte.map((p) => p.projekt_id)))}>
                  {wahl.size === projekte.length ? "Keine" : "Alle"}
                </button>
              )}
              <span className="vg-schreib-von" />
              <button type="button" className="vg-btn vg-btn-ghost" onClick={onClose}>Später</button>
              <button type="button" className="vg-btn vg-btn-primary" disabled={anlegen.isPending || (q.data ? false : true)}
                onClick={() => anlegen.mutate([...wahl])}>
                {anlegen.isPending ? "Legt an …" : wahl.size ? `${wahl.size} Aufgabe${wahl.size === 1 ? "" : "n"} anlegen` : "Keinen fragen"}
              </button>
            </footer>
          </>
        )}
      </section>
    </div>
  );
}
